import React, { useEffect, useMemo, useState } from 'react';

import './index.scss';

import { getLocale, useDispatch, useHistory, useIntl } from 'umi';
import { ConfigProvider, Table } from 'tea-component';
import type { TableColumn } from 'tea-component';
import socialMediaColumns from './columns/social-media-columns';
import gameStoreMobile from './columns/game-store-mobile';
import gameList from './columns/game-list';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getTeaLocale } from '@/pages/home/utils/lang';
import { NO_DATE } from '../../constants';

const { pageable, sortable, scrollable, autotip } = Table.addons;

/**
 * TableCommon 通用表格组件
 * 
 * @deprecated 该组件已废弃，请勿在新代码中使用
 * @deprecated 废弃日期: 2025-01-14
 * @deprecated 废弃原因: 该组件已停止维护
 * @deprecated 替代方案: 请使用 TableCommonHook 或 TableComponents 替代
 * 
 * @param bordered
 * @param list
 * @param tableSticky
 * @param filterBoxValue
 * @param tableType
 * @param pageIndex
 * @param pageSize
 * @param total
 * @param onPagingChange
 * @param sortColumns
 * @param pageSizeOptions
 * @param sortType          排序类型，local:本地排序 和 server：服务端排序，这将参数传出去
 * @param onRecordsChange
 * @returns
 */
export default function TableCommon({
  bordered = undefined,
  list,
  tableSticky,
  verticalTop = true,
  filterBoxValue,
  tableType,
  pageIndex = 1,
  pageSize = 10,
  total = 0,
  onPagingChange = () => {},
  sortColumns = [],
  pageSizeOptions = [10, 20, 30, 50, 100, 200],
  sortType = 'server',
  sortInfo,
  defaultSort = {
    by: 'mention',
    order: 'desc',
  },
  defaultColumns,
  keywordMap,
  onRecordsChange = () => {}, // 真实的数据列表
  minWidth = 1400,
  hasPageable = true,
  loading = false,
  gameInfo = {},
  isSpecialChannel = false,
  excludeInvalidInfo,
}: {
  bordered?: boolean | 'all' | undefined;
  list: Array<any>;
  tableSticky?: boolean;
  verticalTop?: boolean;
  filterBoxValue?: any;
  tableType: 'social-media' | 'game-store-mobile' | 'game-list' | string;
  pageIndex: number;
  pageSize: number;
  total: number;
  onPagingChange: (pageInfo: { pageIndex?: number; pageSize?: number }) => void;
  sortColumns?: Array<any>;
  pageSizeOptions?: Array<number>;
  sortType?: 'server' | 'local';
  defaultSort?: SortBy<any>;
  sortInfo?: [SortBy<any>, (item: SortBy<any>) => void];
  defaultColumns?: { [key: string]: TableColumn<any> };
  onRecordsChange?: (list: Array<any>) => void; // 真实的数据列表
  keywordMap?: any;
  minWidth?: number;
  hasPageable?: boolean;
  loading?: boolean;
  gameInfo?: any;
  isSpecialChannel?: boolean;
  excludeInvalidInfo?: any;
}) {
  // 废弃警告
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'TableCommon is deprecated and will be removed in a future version. Please use TableCommonHook or TableComponents instead.',
    );
  }

  const { formatMessage } = useIntl();

  // 排序
  const [sort, setSort] = sortInfo || useState<SortBy<any>>(defaultSort);

  const [columns, setColumns] = useState<any>([]);

  const history = useHistory();
  const dispatch = useDispatch();

  const getColumns = () => {
    let columns = [];
    switch (tableType) {
      case 'social-media':
        columns = socialMediaColumns({
          formatMessage,
          filterBoxValue,
          gameInfo,
          isSpecialChannel,
          sort,
          setSort,
          excludeInvalidInfo,
        });
        break;
      case 'game-store-mobile':
        columns = gameStoreMobile({
          formatMessage,
          filterBoxValue,
          gameInfo,
          sort,
          setSort,
          excludeInvalidInfo,
        });
        break;
      case 'game-list':
        columns = gameList({
          formatMessage,
          pageInfo: {
            pageIndex,
            pageSize,
          },
          sort,
          setSort,
          history,
          dispatch,
        });
        break;
      default:
        columns = socialMediaColumns({ formatMessage, filterBoxValue });
    }

    if (defaultColumns) {
      columns = columns.map((item) => {
        return defaultColumns[item.key] || item;
      });
    }
    setColumns(columns);
  };

  useEffect(() => {
    getColumns();
  }, [filterBoxValue, getLocale(), sort, pageIndex, pageSize]);

  useEffect(() => {
    if (keywordMap) {
      getColumns();
    }
  }, [keywordMap]);

  const records = useMemo(() => {
    if (sortType === 'server') {
      return list;
    }

    const newList = list?.sort(sortable.comparer([sort])) || [];
    onRecordsChange?.(newList);
    return newList;
  }, [list, sort]);
  const locale = getTeaLocale(getLocale());

  const addons = [
    sortable({
      columns: sortColumns.map((item) => ({ prefer: 'desc', key: item })),
      value: [sort],
      onChange: (value) => {
        let { order, by } = value?.[0] || {};
        let newSort: SortBy<any> = defaultSort;

        if (!by) {
          newSort = { ...sort, order: 'desc' };
        } else if (by === sort.by) {
          newSort = { ...sort, order: order };
        } else if (by !== sort.by) {
          newSort = { order, by };
        }
        setSort(newSort);
      },
    }),
    scrollable({
      minWidth: minWidth,
    }),
    autotip({
      isLoading: loading,
      emptyText: formatMessage({ id: NO_DATE.SELECTION }),
    }),
  ];

  if (hasPageable) {
    addons.push(
      pageable({
        recordCount: total,
        pageIndex,
        pageSize,
        onPagingChange,
        pageSizeOptions,
      }),
    );
  }

  return (
    <ConfigProvider locale={locale}>
      <Table
        className={`dbo-tablecommon ${
          tableSticky ? 'dbo-table-sticky dbo-table-sticky--withfilter' : ''
        }`}
        verticalTop={verticalTop}
        bordered={bordered}
        records={records || []}
        columns={columns}
        addons={addons}
      />
    </ConfigProvider>
  );
}
